import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronDown, LifeBuoy, Send, CheckCircle2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['Problème de lecture', 'Abonnement et facturation', 'Problème de compte', 'Signaler un contenu', 'Question générale', 'Autre'];
const FAQ = [
  { q: 'La lecture d\'une vidéo ne fonctionne pas, que faire ?', a: 'Vérifiez votre connexion internet, puis rechargez la page. Si le problème persiste, essayez un autre navigateur ou appareil. Vous pouvez aussi nous signaler le contenu concerné via le formulaire ci-dessous en précisant le titre.' },
  { q: 'Comment gérer ou résilier mon abonnement ?', a: 'Rendez-vous dans Compte > Abonnement. Vous pouvez y changer d\'offre, résilier (vous gardez l\'accès jusqu\'à la fin de la période payée) ou reprendre votre abonnement à tout moment.' },
  { q: 'Comment modifier mes profils et avatars ?', a: 'Dans Compte > Profils, sélectionnez un profil pour changer son nom, son avatar (catégorie « Modifier mon avatar »), ses filtres de contenu ou son code PIN.' },
  { q: 'J\'ai oublié mon mot de passe', a: 'Sur la page de connexion, cliquez sur « Mot de passe oublié ? » : vous recevrez un email avec un lien sécurisé valable 1 heure pour choisir un nouveau mot de passe.' },
  { q: 'Sur combien d\'appareils puis-je regarder Maya+ ?', a: 'Le nombre d\'écrans simultanés dépend de votre offre. Retrouvez le détail de chaque offre dans Compte > Abonnement > Changer d\'offre.' },
];

export default function HelpPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await axios.post(`${API}/support/contact`, { category, subject, message }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Envoi impossible, réessayez.');
    } finally { setSending(false); }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#E9641A] transition-colors text-sm";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #11041F 0%, #1a0e2e 100%)' }}>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-white mb-8 transition-colors" data-testid="help-back-btn">
          <ArrowLeft size={18} /> Retour
        </button>
        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-xl bg-[#E9641A]/15 flex items-center justify-center"><LifeBuoy size={22} className="text-[#E9641A]" /></span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Aide et support</h1>
        </div>
        <p className="text-white/45 text-sm mb-10">Une question, un problème ? Consultez la FAQ ou contactez directement notre équipe.</p>

        <h2 className="text-lg font-bold text-white mb-4">Questions fréquentes</h2>
        <div className="space-y-2 mb-12">
          {FAQ.map((f, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`help-faq-${i}`}
                className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold text-white hover:bg-white/5 transition-colors">
                {f.q}
                <ChevronDown size={16} className={`text-white/40 transition-transform flex-none ml-3 ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-4 text-sm text-white/55 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>

        <h2 className="text-lg font-bold text-white mb-1">Contacter le support</h2>
        <p className="text-white/45 text-xs mb-5"><Mail size={11} className="inline mr-1" />Notre équipe vous répond par email, généralement sous 24 à 48 h.</p>

        {sent ? (
          <div className="bg-white/[0.03] border border-emerald-500/30 rounded-xl p-8 text-center" data-testid="help-sent">
            <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-bold mb-1">Demande envoyée !</p>
            <p className="text-white/50 text-sm">Un email de confirmation vient de vous être envoyé. Nous revenons vers vous au plus vite.</p>
            <button onClick={() => { setSent(false); setSubject(''); setMessage(''); }} className="mt-5 text-sm text-[#E9641A] font-semibold hover:text-[#FE975E]" data-testid="help-send-another">
              Envoyer une autre demande
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white/[0.03] border border-white/10 rounded-xl p-6 space-y-4" data-testid="help-contact-form">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg" data-testid="help-error">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wider">Catégorie</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className={inputCls} data-testid="help-category">
                  {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#1a0e2e]">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wider">Sujet</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} required maxLength={200}
                  placeholder="Ex : Impossible de lire un épisode" className={inputCls} data-testid="help-subject" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wider">Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} required rows={5} maxLength={3000}
                placeholder="Décrivez votre problème ou votre question le plus précisément possible…" className={`${inputCls} resize-none`} data-testid="help-message" />
            </div>
            <button type="submit" disabled={sending} data-testid="help-submit"
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#E9641A] hover:bg-[#FE975E] text-white font-bold text-sm px-8 py-3 rounded-lg transition-colors disabled:opacity-50">
              {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={15} />}
              Envoyer ma demande
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
